/**
 * WatchlistSection - Smart keyword tracker cards for the Analytics page
 *
 * Shows per-group activity spotted in transaction descriptions (crypto buys,
 * AI subscriptions, anything user-defined): spend/income/transfer totals,
 * trend vs the previous equal-length period, top matches, and term chips
 * that deep-link into History search (/transactions?q=term).
 *
 * Includes an inline editor (add/remove groups and terms, enable/disable).
 * Group names/terms are user data like account names - not translated.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePrivacyMode } from '@hooks/usePrivacyMode';
import { formatCurrency as formatCurrencyIntl, formatMonthDay } from '@/utils/format';
import { useLanguage } from '@/i18n';
import {
  createWatchGroup,
  normalizeTerm,
  WATCHLIST_LIMITS,
  type WatchGroup,
  type WatchGroupStats,
} from '@services/watchlist';
import './WatchlistSection.css';

interface WatchlistSectionProps {
  stats: WatchGroupStats[];
  groups: WatchGroup[];
  loading: boolean;
  onUpdateGroups: (groups: WatchGroup[]) => void;
}

function fmt(amount: number): string {
  return formatCurrencyIntl(amount, 'USD', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function Amount({ value, privacy, prefix = '' }: { value: number; privacy: boolean; prefix?: string }) {
  if (privacy) return <span className="private-amount masked">{prefix}••••</span>;
  return <>{prefix}{fmt(value)}</>;
}

// ============================================================
// Group card (view mode)
// ============================================================

function GroupCard({ stat }: { stat: WatchGroupStats }) {
  const { t } = useLanguage();
  const { isPrivacyMode } = usePrivacyMode();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const { group } = stat;
  const hasActivity = stat.count > 0;
  const hitTerms = Object.entries(stat.termHits).sort((a, b) => b[1] - a[1]);

  return (
    <div className={`watch-card ${hasActivity ? '' : 'watch-card--quiet'}`}>
      <button
        className="watch-card-head"
        onClick={() => hasActivity && setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="watch-icon">{group.icon}</span>
        <span className="watch-name">{group.name}</span>
        {group.excludeFromSpending && (
          <span className="watch-capital-tag" title={t('analytics.watchlist.capital_tag_hint')}>
            {t('analytics.watchlist.capital_tag')}
          </span>
        )}
        {hasActivity ? (
          <span className="watch-count">{t('analytics.watchlist.matches', { count: stat.count })}</span>
        ) : (
          <span className="watch-count watch-count--quiet">{t('analytics.watchlist.no_matches')}</span>
        )}
        {hasActivity && (
          <span className={`watch-chevron ${expanded ? 'open' : ''}`} aria-hidden="true">▾</span>
        )}
      </button>

      {hasActivity && (
        <div className="watch-totals">
          {/* Capital-move groups are asset reallocation, not consumption -
              say "Moved out/back" in neutral colors, never "Spent" in red,
              and show the net still parked outside (the number the user
              actually thinks in: "crypto/defi: $8.7k"). */}
          {stat.expense > 0 && (
            <div className="watch-total">
              <span className="watch-total-label">
                {group.excludeFromSpending
                  ? t('analytics.watchlist.moved_out')
                  : t('analytics.watchlist.spent')}
              </span>
              <span className={`watch-total-value ${group.excludeFromSpending ? 'watch-total-value--capital' : 'amount--expense'}`}>
                <Amount value={stat.expense} privacy={isPrivacyMode} prefix={group.excludeFromSpending ? '' : '-'} />
              </span>
            </div>
          )}
          {stat.income > 0 && (
            <div className="watch-total">
              <span className="watch-total-label">
                {group.excludeFromSpending
                  ? t('analytics.watchlist.moved_back')
                  : t('analytics.watchlist.received')}
              </span>
              <span className={`watch-total-value ${group.excludeFromSpending ? 'watch-total-value--capital' : 'amount--income'}`}>
                <Amount value={stat.income} privacy={isPrivacyMode} prefix={group.excludeFromSpending ? '' : '+'} />
              </span>
            </div>
          )}
          {group.excludeFromSpending && stat.expense - stat.income > 0 && (
            <div className="watch-total">
              <span className="watch-total-label">{t('analytics.watchlist.net_out')}</span>
              <span className="watch-total-value watch-total-value--capital watch-total-value--strong">
                <Amount value={stat.expense - stat.income} privacy={isPrivacyMode} />
              </span>
            </div>
          )}
          {stat.transfer > 0 && (
            <div className="watch-total">
              <span className="watch-total-label">{t('analytics.watchlist.transfers')}</span>
              <span className="watch-total-value">
                <Amount value={stat.transfer} privacy={isPrivacyMode} />
              </span>
            </div>
          )}
          {stat.changePct !== null && Math.abs(stat.changePct) >= 1 && (
            <span className={`watch-trend ${stat.changePct > 0 ? 'watch-trend--up' : 'watch-trend--down'}`}>
              {stat.changePct > 0 ? '↑' : '↓'}{' '}
              {t('analytics.watchlist.vs_prev', { percent: Math.abs(stat.changePct).toFixed(0) })}
            </span>
          )}
          {stat.hasBaseline && stat.changePct === null && stat.expense > 0 && (
            <span className="watch-trend watch-trend--new">{t('analytics.watchlist.new_activity')}</span>
          )}
        </div>
      )}

      {expanded && hasActivity && (
        <div className="watch-detail">
          {stat.topMatches.length > 0 && (
            <div className="watch-matches">
              {stat.topMatches.map((m) => (
                <div key={m.id} className="watch-match-row">
                  <span className="watch-match-desc">{m.description}</span>
                  <span className="watch-match-date">{formatMonthDay(m.date)}</span>
                  {/* Capital groups: neutral amounts, no minus - these rows
                      are asset moves, not losses */}
                  <span
                    className={`watch-match-amount ${
                      group.excludeFromSpending
                        ? 'watch-total-value--capital'
                        : m.type === 'income' ? 'amount--income' : m.type === 'expense' ? 'amount--expense' : ''
                    }`}
                  >
                    <Amount
                      value={m.amount}
                      privacy={isPrivacyMode}
                      prefix={
                        group.excludeFromSpending
                          ? m.type === 'income' ? '+' : ''
                          : m.type === 'income' ? '+' : m.type === 'expense' ? '-' : ''
                      }
                    />
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="watch-term-chips">
            {hitTerms.map(([term, hits]) => (
              <button
                key={term}
                className="watch-term-chip"
                title={t('analytics.watchlist.search_hint')}
                onClick={() => navigate(`/transactions?q=${encodeURIComponent(term)}`)}
              >
                {term} <span className="watch-term-hits">{hits}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Editor
// ============================================================

function GroupEditor({
  groups,
  onUpdateGroups,
}: {
  groups: WatchGroup[];
  onUpdateGroups: (groups: WatchGroup[]) => void;
}) {
  const { t } = useLanguage();
  const [newGroupName, setNewGroupName] = useState('');
  const [termDrafts, setTermDrafts] = useState<Record<string, string>>({});

  const patchGroup = (id: string, patch: Partial<WatchGroup>) => {
    onUpdateGroups(groups.map((g) => (g.id === id ? { ...g, ...patch } : g)));
  };

  const addTerm = (group: WatchGroup) => {
    const term = normalizeTerm(termDrafts[group.id] || '');
    if (!term || group.terms.includes(term)) return;
    if (group.terms.length >= WATCHLIST_LIMITS.maxTermsPerGroup) return;
    patchGroup(group.id, { terms: [...group.terms, term] });
    setTermDrafts((d) => ({ ...d, [group.id]: '' }));
  };

  const addGroup = () => {
    const name = newGroupName.trim();
    if (!name || groups.length >= WATCHLIST_LIMITS.maxGroups) return;
    onUpdateGroups([...groups, createWatchGroup(name)]);
    setNewGroupName('');
  };

  return (
    <div className="watch-editor">
      {groups.map((group) => (
        <div key={group.id} className={`watch-editor-group ${group.enabled ? '' : 'watch-editor-group--off'}`}>
          <div className="watch-editor-head">
            <span className="watch-icon">{group.icon}</span>
            <input
              className="watch-editor-name"
              value={group.name}
              maxLength={WATCHLIST_LIMITS.maxNameLength}
              onChange={(e) => patchGroup(group.id, { name: e.target.value })}
              aria-label={t('analytics.watchlist.group_name_aria')}
            />
            <button
              className={`watch-editor-toggle ${group.enabled ? 'on' : ''}`}
              onClick={() => patchGroup(group.id, { enabled: !group.enabled })}
            >
              {group.enabled ? t('analytics.watchlist.on') : t('analytics.watchlist.off')}
            </button>
            <button
              className="watch-editor-delete"
              onClick={() => onUpdateGroups(groups.filter((g) => g.id !== group.id))}
              aria-label={t('analytics.watchlist.delete_group_aria')}
            >
              ×
            </button>
          </div>
          <div className="watch-editor-terms">
            {group.terms.map((term) => (
              <span key={term} className="watch-editor-term">
                {term}
                <button
                  className="watch-editor-term-remove"
                  onClick={() => patchGroup(group.id, { terms: group.terms.filter((x) => x !== term) })}
                  aria-label={t('analytics.watchlist.remove_term_aria')}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              className="watch-editor-term-input"
              value={termDrafts[group.id] || ''}
              maxLength={WATCHLIST_LIMITS.maxTermLength}
              placeholder={t('analytics.watchlist.add_term_placeholder')}
              onChange={(e) => setTermDrafts((d) => ({ ...d, [group.id]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTerm(group);
                }
              }}
              onBlur={() => addTerm(group)}
            />
          </div>
          <label className="watch-editor-capital">
            <input
              type="checkbox"
              checked={!!group.excludeFromSpending}
              onChange={(e) => patchGroup(group.id, { excludeFromSpending: e.target.checked })}
            />
            <span className="watch-editor-capital-text">
              <span>{t('analytics.watchlist.capital_toggle')}</span>
              <span className="watch-editor-capital-hint">{t('analytics.watchlist.capital_toggle_hint')}</span>
            </span>
          </label>
        </div>
      ))}

      {groups.length < WATCHLIST_LIMITS.maxGroups && (
        <div className="watch-editor-add">
          <input
            className="watch-editor-name"
            value={newGroupName}
            maxLength={WATCHLIST_LIMITS.maxNameLength}
            placeholder={t('analytics.watchlist.group_name_placeholder')}
            onChange={(e) => setNewGroupName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addGroup();
              }
            }}
          />
          <button className="watch-editor-add-btn" onClick={addGroup} disabled={!newGroupName.trim()}>
            {t('analytics.watchlist.add_group')}
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Section
// ============================================================

export function WatchlistSection({ stats, groups, loading, onUpdateGroups }: WatchlistSectionProps) {
  const { t } = useLanguage();
  const [editing, setEditing] = useState(false);

  return (
    <div className="watchlist-section">
      {editing ? (
        <GroupEditor groups={groups} onUpdateGroups={onUpdateGroups} />
      ) : loading ? (
        <div className="list-card">
          <div className="empty-state">{t('analytics.watchlist.analyzing')}</div>
        </div>
      ) : groups.length === 0 ? (
        <div className="list-card">
          <div className="empty-state">
            <span className="empty-icon">🔎</span>
            <span className="empty-text">{t('analytics.watchlist.empty')}</span>
            <span className="empty-hint">{t('analytics.watchlist.empty_hint')}</span>
          </div>
        </div>
      ) : (
        <div className="watch-cards">
          {stats.map((stat) => (
            <GroupCard key={stat.group.id} stat={stat} />
          ))}
        </div>
      )}

      <div className="watch-footer">
        <button className={`watch-edit-btn ${editing ? 'active' : ''}`} onClick={() => setEditing(!editing)}>
          {editing ? t('analytics.watchlist.done') : t('analytics.watchlist.edit')}
        </button>
      </div>
    </div>
  );
}

export default WatchlistSection;
